// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { ReactNode } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Users, ShieldCheck, Mail, ScrollText, MessageSquare, Building2, KeyRound, ShieldQuestion, Network, LayoutDashboard, FileBarChart, LayoutTemplate, DollarSign, Plug, Zap, Clock , Gauge } from 'lucide-react'
import { useAuth } from '../../auth/AuthContext'
import { usePharmacy } from '../../auth/PharmacyContext'

/**
 * /admin/* layout. Renders a horizontal tab strip + the routed sub-page
 * underneath. Tabs are filtered by permission so a user only sees the
 * sections they can access.
 *
 * WI-900: tabs are ALSO filtered by mode. Pharmacy admins (mode='pharmacy'
 * with admin.* perms via the Owner role) see only the tenant-scoped admin
 * tools — Users, Roles, Invitations. They never see the SaaS-only tabs
 * (Pharmacies onboarding, system Audit, Feedback inbox, PioneerRx config,
 * API keys) even though their permissions would technically allow it.
 *
 * The AppShell sidebar already shows top-level admin links — this layout
 * is the secondary navigation when the user is in the admin section.
 */
export default function AdminLayout() {
  const { me } = useAuth()
  const { mode } = usePharmacy()
  const has = (p: string) => Boolean(me?.permissions?.includes(p))

  // saasOnly tabs ONLY render when the actor is in SaaS mode (an Owl-side
  // operator with no pharmacy_id). A pharmacy Owner doesn't see them.
  const isSaas = mode === 'saas'

  const tabs: { to: string; label: string; icon: any; show: boolean; end?: boolean }[] = [
    { to: '/admin',              label: 'System Monitor', icon: LayoutDashboard, show: isSaas && has('admin.users.read'), end: true },
    { to: '/admin/pharmacies',   label: 'Pharmacies',   icon: Building2,     show: isSaas && has('admin.users.read') },
    { to: '/admin/users',        label: 'Users',        icon: Users,         show: has('admin.users.read') },
    { to: '/admin/roles',        label: 'Roles',        icon: ShieldCheck,   show: has('admin.roles.read') },
    { to: '/admin/groups',       label: 'Groups',       icon: Users,         show: has('admin.users.read') },   // D-RBAC-1
    { to: '/admin/invitations',  label: 'Invitations',  icon: Mail,          show: has('admin.users.invite') },
    { to: '/admin/audit',        label: 'Audit log',    icon: ScrollText,    show: has('admin.audit.read') },
    { to: '/admin/data-requests',label: 'Data requests',icon: ShieldQuestion,show: has('admin.audit.read') },
    { to: '/admin/feedback',     label: 'Feedback',     icon: MessageSquare, show: isSaas && has('admin.feedback.read') },
    { to: '/admin/api-keys',     label: 'API keys',     icon: KeyRound,      show: isSaas && has('account.api_keys.self') },
    { to: '/admin/network-security', label: 'Network Security', icon: Network, show: isSaas && has('admin.users.read') },
    { to: '/admin/security',     label: 'Security',      icon: ShieldCheck,   show: isSaas && has('admin.users.read') },
    { to: '/admin/compliance',   label: 'Compliance',    icon: ScrollText,    show: isSaas && has('admin.users.read') },
    { to: '/admin/cost',         label: 'Cost management', icon: DollarSign, show: isSaas && has('admin.users.read') },
    { to: '/admin/profitability', label: 'Profitability', icon: DollarSign, show: isSaas && has('admin.users.read') },
    { to: '/admin/resources',    label: 'Resources', icon: Zap, show: isSaas && has('admin.users.read') },
    { to: '/admin/workers',      label: 'Background workers', icon: Clock, show: isSaas && has('admin.users.read') },
    { to: '/admin/api-config',   label: 'API config',    icon: Plug,          show: isSaas && has('admin.users.read') },
    { to: '/admin/data-api',     label: 'Data API',      icon: Gauge,         show: isSaas && has('admin.users.read') },
    { to: '/admin/reports',      label: 'Report builder', icon: FileBarChart, show: isSaas && has('admin.users.read') },
    { to: '/admin/reports-ai',   label: 'AI Report Builder', icon: FileBarChart, show: isSaas && has('admin.users.read') },
    { to: '/admin/reports-ai-prompt', label: 'AI Prompt', icon: ScrollText, show: isSaas && has('admin.users.read') },
    { to: '/admin/rx-view',      label: 'Rx view builder', icon: LayoutTemplate, show: isSaas && has('admin.users.read') },
    { to: '/admin/home-view',    label: 'Home dashboard builder', icon: LayoutTemplate, show: isSaas && has('admin.users.read') },
    { to: '/admin/patient-view', label: 'Patient view builder', icon: LayoutTemplate, show: isSaas && has('admin.users.read') },
    { to: '/admin/prescriber-view', label: 'Prescriber view builder', icon: LayoutTemplate, show: isSaas && has('admin.users.read') },
    { to: '/admin/facility-view', label: 'Facility view builder', icon: LayoutTemplate, show: isSaas && has('admin.users.read') },
  ].filter(t => t.show)

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <header style={headerStyle}>
        <div style={{ maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%', padding: '24px 24px 0' }}>
          <h1 style={titleStyle}>Administration</h1>
          <nav style={tabRow} aria-label="Admin sections">
            {tabs.map(t => {
              const Icon = t.icon
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end ?? false}
                  style={({ isActive }) => ({
                    ...tabStyle,
                    ...(isActive ? tabActiveStyle : null),
                  })}
                >
                  <Icon size={15} aria-hidden="true" />
                  <span>{t.label}</span>
                </NavLink>
              )
            })}
          </nav>
        </div>
      </header>

      {/* overflowX:auto restores horizontal scrolling for wide admin content (tables, builders):
          the app shell is overflow-x:hidden (index.css), so anything wider than this wrapper was
          silently CLIPPED on desktop — the mobile-only table rule (<=720px) never applied there. */}
      <div style={{ flex: 1, padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%', overflowX: 'auto' }}>
        <Outlet />
      </div>
    </div>
  )
}

// Re-usable section card for tab content
export function SectionCard({ children }: { children: ReactNode }) {
  return <div style={cardStyle}>{children}</div>
}

const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  borderBottom: '1px solid var(--color-border)',
}
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700,
  margin: 0, letterSpacing: -0.5,
}
const tabRow: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginTop: 16,
  flexWrap: 'wrap',
}
const tabStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 14px',
  fontSize: 13,
  color: 'var(--color-text-soft)',
  textDecoration: 'none',
  borderBottom: '2px solid transparent',
  marginBottom: -1,
  fontWeight: 500,
}
const tabActiveStyle: React.CSSProperties = {
  color: 'var(--color-text)',
  borderBottomColor: 'var(--color-primary)',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 0,
  // Was overflow:'hidden' (corner clipping) — that also amputated wide tables with no
  // scrollbar. auto keeps the rounded-corner clip AND makes overflow reachable.
  overflowX: 'auto',
}
